import type { Etape } from "./contract";

/**
 * Les petites marques de la console : le badge d'etape, son point (la legende, dans les
 * filtres) et la pastille de type. Elles vivent ici pour que la table, les filtres et les
 * deux details disent la meme chose de la meme facon.
 *
 * Les couleurs viennent de `theme.css` (section 2). Les classes sont ecrites en entier :
 * Tailwind ne garde que les noms de classe qu'il lit tels quels dans le source.
 */

type Ton = { readonly badge: string; readonly point: string };

/** Avant le test : le pari existe, rien ne tourne encore. */
const NEUTRE: Ton = {
  badge: "bg-muted text-muted-foreground",
  point: "bg-muted-foreground",
};

/** Parque ou tue : un contour, pas de fond. */
const RETRAIT: Ton = {
  badge: "border border-border text-muted-foreground",
  point: "border border-muted-foreground",
};

const TONS: Record<Etape, Ton> = {
  idée: NEUTRE,
  triée: NEUTRE,
  grillée: NEUTRE,
  "en test": { badge: "bg-etape-test/15 text-etape-test", point: "bg-etape-test" },
  build: { badge: "bg-etape-build/15 text-etape-build", point: "bg-etape-build" },
  live: { badge: "bg-etape-live/15 text-etape-live", point: "bg-etape-live" },
  parquée: RETRAIT,
  tuée: RETRAIT,
};

/** Un pari parque ou tue ne demande plus d'attention : sa ligne passe en retrait. */
export function estEnRetrait(etape: Etape): boolean {
  return etape === "parquée" || etape === "tuée";
}

export function BadgeEtape({ etape }: { etape: Etape }) {
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${TONS[etape].badge}`}
    >
      {etape}
    </span>
  );
}

/**
 * Le point de couleur d'une etape, pose dans son filtre : la rangee de filtres sert de
 * legende a la table. Sur un filtre presse le fond s'inverse, le point suit donc le texte.
 */
export function PointEtape({ etape, presse = false }: { etape: Etape; presse?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`size-2 shrink-0 rounded-full ${presse ? "bg-current" : TONS[etape].point}`}
    />
  );
}

/** L'etiquette d'un geste : son type (`typeEnClair`), ou « relance ». */
export function Pastille({ children }: { children: string }) {
  return (
    <span className="rounded bg-muted px-2 py-0.5 text-xs uppercase text-muted-foreground">
      {children}
    </span>
  );
}
